import React, { FC, useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  Stack,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import SettingsIcon from '@mui/icons-material/Settings'
import { PortalLayout } from '@/apps/legacy/components/portal/PortalLayout'
import { PortalPageHeader } from '@/apps/legacy/components/portal/PortalPageHeader'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { FONT_MONO } from '@/shared/styles/typography'
import {
  useLineSettingsQuery,
  useUpdateLineSettingsMutation,
  useVerifyLineSettingsMutation,
} from '@/features/internal/patient-messaging'
import { useToast } from '@/shared/contexts/ToastContext'

const LineSettingsPage: FC = () => {
  const { data: settings, isLoading } = useLineSettingsQuery()
  const updateMutation = useUpdateLineSettingsMutation()
  const verifyMutation = useVerifyLineSettingsMutation()
  const { showToast } = useToast()

  const [channelAccessToken, setChannelAccessToken] = useState('')
  const [channelSecret, setChannelSecret] = useState('')
  const [botBasicId, setBotBasicId] = useState('')
  const [copied, setCopied] = useState(false)

  const handleCopyWebhookUrl = async () => {
    if (settings?.webhook_url) {
      await navigator.clipboard.writeText(settings.webhook_url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleSave = () => {
    // Basic ID が無いと連携用の QR を作れないので 3 つとも必須 (保存済みの Basic ID は打ち直さなくてよい)
    const basicId = botBasicId || settings?.bot_basic_id || ''
    if (!channelAccessToken || !channelSecret || !basicId) {
      showToast(
        'Channel Access Token・Channel Secret・Bot Basic ID は必須です',
        'error'
      )
      return
    }
    updateMutation.mutate(
      {
        channel_access_token: channelAccessToken,
        channel_secret: channelSecret,
        bot_basic_id: basicId,
      },
      {
        onSuccess: () => {
          showToast('LINE連携設定を保存しました', 'success')
          setChannelAccessToken('')
          setChannelSecret('')
        },
        onError: () => {
          showToast('保存に失敗しました', 'error')
        },
      }
    )
  }

  const handleVerify = () => {
    verifyMutation.mutate(undefined, {
      onSuccess: (data) => {
        showToast(`接続検証に成功しました（Bot: ${data.bot_name}）`, 'success')
      },
      onError: () => {
        showToast(
          'LINE API接続に失敗しました。設定を確認してください。',
          'error'
        )
      },
    })
  }

  const getStatusChip = () => {
    if (!settings) return null
    if (settings.is_verified) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="検証済み"
          color="success"
          size="small"
        />
      )
    }
    if (settings.channel_access_token) {
      return <Chip label="設定済み（未検証）" color="warning" size="small" />
    }
    return <Chip label="未設定" color="default" size="small" />
  }

  if (isLoading) {
    return (
      <PortalLayout>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      </PortalLayout>
    )
  }

  return (
    <PortalLayout>
      <PortalPageHeader title="LINE連携設定" icon={<SettingsIcon />} />

      <Stack spacing={3}>
        {/* ステータス */}
        <Card sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 2,
            }}
          >
            <Typography variant="subtitle1">接続ステータス</Typography>
            {getStatusChip()}
          </Box>
          {settings?.is_verified && (
            <Alert severity="success" sx={{ mt: 1 }}>
              LINE Bot APIとの接続が確認されています。
            </Alert>
          )}
        </Card>

        {/* Webhook URL */}
        <Card sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Webhook URL
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            LINE Developers コンソールの Messaging API 設定で、以下の URL を
            Webhook URL に設定してください。
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: colorTokens.grey[50],
              p: 1.5,
              borderRadius: 1,
              border: `1px solid ${colorTokens.border.light}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                fontFamily: FONT_MONO.fontFamily,
                wordBreak: 'break-all',
              }}
            >
              {settings?.webhook_url}
            </Typography>
            <Tooltip title={copied ? 'コピーしました' : 'コピー'}>
              <IconButton onClick={handleCopyWebhookUrl} size="small">
                {copied ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Card>

        {/* チャネル情報入力 */}
        <Card sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            チャネル情報
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            LINE Developers
            コンソールから取得したチャネル情報を入力してください。
            既に設定済みの場合、現在の値はマスキングされて表示されます。
          </Typography>
          <Stack spacing={2.5}>
            <TextField
              label="Channel Access Token"
              value={channelAccessToken}
              onChange={(e) => setChannelAccessToken(e.target.value)}
              placeholder={settings?.channel_access_token || '未設定'}
              fullWidth
              size="small"
              helperText="Messaging API設定 > チャネルアクセストークン（長期）"
            />
            <TextField
              label="Channel Secret"
              value={channelSecret}
              onChange={(e) => setChannelSecret(e.target.value)}
              placeholder={settings?.channel_secret || '未設定'}
              fullWidth
              size="small"
              helperText="チャネル基本設定 > チャネルシークレット"
            />
            <TextField
              label="Bot Basic ID"
              value={botBasicId}
              onChange={(e) => setBotBasicId(e.target.value)}
              placeholder={settings?.bot_basic_id || '未設定（例: @150dsllm）'}
              fullWidth
              size="small"
              helperText="Messaging API設定 > ボットのベーシックID"
            />
          </Stack>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mt: 3,
            }}
          >
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={
                updateMutation.isPending ||
                (!channelAccessToken && !channelSecret)
              }
            >
              {updateMutation.isPending ? '保存中...' : '保存'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleVerify}
              disabled={
                verifyMutation.isPending || !settings?.channel_access_token
              }
            >
              {verifyMutation.isPending ? '検証中...' : '接続検証'}
            </Button>
          </Box>
        </Card>
      </Stack>
    </PortalLayout>
  )
}

export default LineSettingsPage
